import { useCallback } from "react";
import { toast } from "react-hot-toast";
import { ContactInfo, RecipientItem } from "../../../types";
import { ContactType } from "../types";

interface UseContactSelectionProps {
  contactInfo: ContactInfo;
  setContactInfo: React.Dispatch<React.SetStateAction<ContactInfo>>;
  setSelectedCandidate: React.Dispatch<React.SetStateAction<RecipientItem | null>>;
}

export const useContactSelection = ({
  contactInfo,
  setContactInfo,
  setSelectedCandidate
}: UseContactSelectionProps) => {
  const handleContactSelection = useCallback((
    type: ContactType,
    value: string,
    recipient?: RecipientItem
  ) => {
    const isEmail = type === "email";
    const hadOtherContact = isEmail ? !!contactInfo.phone : !!contactInfo.email;
    
    const newContactInfo = isEmail 
      ? { email: value, phone: "", subChannel: "" }
      : { email: "", phone: value, subChannel: contactInfo.subChannel || "" };
    
    setContactInfo(newContactInfo);
    
    if (recipient) {
      setSelectedCandidate(recipient);
    }
    
    const action = hadOtherContact ? "Switched to" : "Selected";
    toast.success(`${action} ${type}: ${value}`);
  }, [contactInfo, setContactInfo, setSelectedCandidate]);

  const clearSelection = useCallback(() => {
    setContactInfo({ email: "", phone: "", subChannel: "" });
    setSelectedCandidate(null);
    toast.success("All selections cleared");
  }, [setContactInfo, setSelectedCandidate]);

  const removeSelection = useCallback((type: ContactType) => {
    if (type === "phone") {
      setContactInfo(prev => ({ ...prev, phone: "", subChannel: "" }));
    } else {
      setContactInfo(prev => ({ ...prev, email: "" }));
    }
    setSelectedCandidate(null);
    toast.success(`${type === "email" ? "Email" : "Phone"} selection removed`);
  }, [setContactInfo, setSelectedCandidate]);

  return {
    handleContactSelection,
    clearSelection,
    removeSelection
  };
};